import React from 'react';
import '../../../../css/role-style.css';
import { Button, Card, Checkbox, Form, Input, Select } from 'antd';
import { StaffRole, StaffRoleUpdateInput } from '../../../../generated';

const { Option } = Select;
interface EditRoleProps {
  onSubmit: (payload: any) => void;
  roleSubtype: string;
  data: StaffRole;
  updateStaffRoleLoading: boolean;
}

export const RoleEdit: React.FC<EditRoleProps> = (props) => {
  const dropDownValue = [
    {
      label: 'IS',
      value: 'Staff.IS'
    },
    {
      label: 'Case Worker',
      value: 'Staff.CaseWorker'
    },
    {
      label: 'Finance',
      value: 'Staff.Finance'
    },
    {
      label: 'ReadOnly',
      value: 'Staff.ReadOnly'
    }
  ];

  const casePermissionFields = [
    'canViewUnassignedCases',
    'canViewCasesAssignedToOthers',
    'canBeAssignedCases',
    'canAssignSelf',
    'canUnassignSelf',
    'canAssignCaseWorker',
    'canUnassignCaseWorker',
    'canIssueRefunds',
    'canViewCaseHistory',
    'canReopenCases'
  ];

  const permissionSections = {
    idCase: {
      path: ['permissions', 'idCasePermissions'],
      searchField: 'canSearchCases',
      fields: casePermissionFields
    },
    credCase: {
      path: ['permissions', 'credCasePermissions'],
      searchField: 'canSearchCases',
      fields: casePermissionFields
    },
    sendCase: {
      path: ['permissions', 'sendCasePermissions'],
      searchField: 'canSearchCases',
      fields: casePermissionFields
    },
    role: {
      path: ['permissions', 'rolePermissions'],
      searchField: 'canSearchRoles',
      fields: ['canAddRoles', 'canEditRoles']
    },
    staff: {
      path: ['permissions', 'staffUserPermissions'],
      searchField: 'canSearchStaff',
      fields: ['canAssignRoles', 'canBlockAccess', 'canAddOrRemoveTags']
    },
    applicant: {
      path: ['permissions', 'applicantUserPermissions'],
      searchField: 'canSearchApplicants',
      fields: ['canUpdatePersonalInformation', 'canBlockAccess', 'canAddOrRemoveTags']
    },
    entity: {
      path: ['permissions', 'entityPermissions'],
      searchField: 'canSearchEntities',
      fields: ['canAddEntities', 'canEditEntities', 'canDeleteEntities']
    }
  };

  const [form] = Form.useForm<StaffRoleUpdateInput>();
  const [formLoading, setFormLoading] = React.useState(false);

  const unCheckPermissions = (section: keyof typeof permissionSections) => {
    const config = permissionSections[section];
    const update = {
      permissions: {
        [config.path[1]]: {
          [config.searchField]: false,
          ...Object.fromEntries(config.fields.map((field) => [field, false]))
        }
      }
    };
    form.setFieldsValue(update);
  };

  const onPermissionChange = (changedValues: any) => {
    for (const [section, config] of Object.entries(permissionSections)) {
      const changes = changedValues.permissions?.[config.path[1]];
      if (!changes) {
        continue;
      }

      // select search permission if any other permission is selected
      if (!Object.keys(changes).some((key) => key.includes('canSearch'))) {
        form.setFieldsValue({ permissions: { [config.path[1]]: { [config.searchField]: true } } });
      }
      // unselect all permissions if search permission is unselected
      if (changes[config.searchField] === false) {
        unCheckPermissions(section as keyof typeof permissionSections);
      }
      return;
    }
  };

  return (
    <div className="container">
      <div className="pageHeading"></div>
      <Form
        initialValues={props.data}
        form={form}
        onFinish={(values) => {
          setFormLoading(true);
          props.onSubmit(values);
          setFormLoading(false);
        }}
        onValuesChange={onPermissionChange}
        layout="vertical"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'right',
          justifyContent: 'end',
          marginTop: '-23px'
        }}
      >
        <Card style={{ marginBottom: '10px', padding: '20px 50px' }}>
          {/* <div className="formContainer"> */}
          <div className="roleContainer">
            <div className="roleInfo">
              <div className="sectionName">
                <h3>Role Info</h3>
              </div>
              <div className="roleInputs">
                <div className="formBox">
                  <Form.Item label="Role Name" style={{ width: '100%' }} rules={[{ required: true, message: 'Entity Name is Required' }]} name={'roleName'}>
                    <Input disabled={props.data.isDefault} />
                  </Form.Item>
                </div>
                {props.roleSubtype === 'Staff.IS' ? (
                  <div className="formBox">
                    <Form.Item
                      label="Role Sub Type"
                      style={{ width: '100%' }}
                      rules={[
                        {
                          required: true,
                          message: 'Role Sub type is Required'
                        }
                      ]}
                      name={'roleSubtype'}
                    >
                      <Select disabled={props.data.isDefault}>
                        {dropDownValue?.map((subRole) => (
                          <Option value={subRole.value} key={subRole?.value}>
                            {subRole.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div className="rolePermission">
              <div className="sectionName">
                <h3>Permissions</h3>
              </div>
              <div className="roleInputs">
                {/* ID Case */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>ID Case</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'idCasePermissions', 'canSearchCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel"> Can Search Cases</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'idCasePermissions', 'canViewUnassignedCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Unassigned Cases</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'idCasePermissions', 'canViewCasesAssignedToOthers']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Cases Assigned to Others</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="permissionSubSectionInput">
                      <div className="formBox">
                        <Form.Item name={['permissions', 'idCasePermissions', 'canBeAssignedCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (!isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    idCasePermissions: {
                                      canAssignSelf: false,
                                      canUnassignSelf: false
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Be Assigned Cases</p>
                          </Checkbox>
                        </Form.Item>
                      </div>
                      <div className="formBox deep">
                        <Form.Item name={['permissions', 'idCasePermissions', 'canAssignSelf']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    idCasePermissions: {
                                      canBeAssignedCases: true
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Assign Self</p>
                          </Checkbox>
                        </Form.Item>
                      </div>
                      <div className="formBox deep">
                        <Form.Item name={['permissions', 'idCasePermissions', 'canUnassignSelf']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    idCasePermissions: {
                                      canBeAssignedCases: true
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Unassign Self</p>
                          </Checkbox>
                        </Form.Item>
                      </div>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'idCasePermissions', 'canAssignCaseWorker']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Assign Case Worker</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'idCasePermissions', 'canUnassignCaseWorker']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Unassign Case Worker</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>

                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'idCasePermissions', 'canIssueRefunds']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Issue Refund</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'idCasePermissions', 'canViewCaseHistory']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Case History</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'idCasePermissions', 'canReopenCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Reopen Case</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </div>

                {/* Verification Case */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Verification Case</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'credCasePermissions', 'canSearchCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel"> Can Search Cases</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'credCasePermissions', 'canViewUnassignedCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Unassigned Cases</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item
                        name={['permissions', 'credCasePermissions', 'canViewCasesAssignedToOthers']}
                        valuePropName="checked"
                        style={{ margin: '0px', padding: '0px' }}
                      >
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Cases Assigned to Others</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                    <Form.Item name={['permissions', 'credCasePermissions', 'canBeAssignedCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (!isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    credCasePermissions: {
                                      canAssignSelf: false,
                                      canUnassignSelf: false
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Be Assigned Cases</p>
                          </Checkbox>
                        </Form.Item>
                    </div>
                    <div className="formBox deep">
                    <Form.Item name={['permissions', 'credCasePermissions', 'canAssignSelf']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    credCasePermissions: {
                                      canBeAssignedCases: true
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Assign Self</p>
                          </Checkbox>
                        </Form.Item>
                    </div>
                    <div className="formBox deep">
                    <Form.Item name={['permissions', 'credCasePermissions', 'canUnassignSelf']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    credCasePermissions: {
                                      canBeAssignedCases: true
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Unassign Self</p>
                          </Checkbox>
                        </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'credCasePermissions', 'canAssignCaseWorker']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Assign Case Worker</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'credCasePermissions', 'canUnassignCaseWorker']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Unassign Case Worker</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>

                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'credCasePermissions', 'canIssueRefunds']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Issue Refund</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'credCasePermissions', 'canViewCaseHistory']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Case History</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'credCasePermissions', 'canReopenCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Reopen Case</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </div>

                {/* Send Case */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Send Case</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'sendCasePermissions', 'canSearchCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel"> Can Search Cases</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'sendCasePermissions', 'canViewUnassignedCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Unassigned Cases</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item
                        name={['permissions', 'sendCasePermissions', 'canViewCasesAssignedToOthers']}
                        valuePropName="checked"
                        style={{ margin: '0px', padding: '0px' }}
                      >
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Cases Assigned to Others</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                    <Form.Item name={['permissions', 'sendCasePermissions', 'canBeAssignedCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (!isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    sendCasePermissions: {
                                      canAssignSelf: false,
                                      canUnassignSelf: false
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Be Assigned Cases</p>
                          </Checkbox>
                        </Form.Item>
                    </div>
                    <div className="formBox deep">
                    <Form.Item name={['permissions', 'sendCasePermissions', 'canAssignSelf']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    sendCasePermissions: {
                                      canBeAssignedCases: true
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Assign Self</p>
                          </Checkbox>
                        </Form.Item>
                    </div>
                    <div className="formBox deep">
                    <Form.Item name={['permissions', 'sendCasePermissions', 'canUnassignSelf']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                          <Checkbox
                            disabled={props.data.isDefault}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                form.setFieldsValue({
                                  permissions: {
                                    sendCasePermissions: {
                                      canBeAssignedCases: true
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <p className="checkBoxLabel">Can Unassign Self</p>
                          </Checkbox>
                        </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'sendCasePermissions', 'canAssignCaseWorker']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Assign Case Worker</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'sendCasePermissions', 'canUnassignCaseWorker']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Unassign Case Worker</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>

                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'sendCasePermissions', 'canIssueRefunds']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Issue Refund</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'sendCasePermissions', 'canViewCaseHistory']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can View Case History</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'sendCasePermissions', 'canReopenCases']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Reopen Case</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* <div className="formContainer"> */}
        <Card style={{ marginBottom: '10px', padding: '20px 50px' }}>
          <div className="roleContainer">
            <div className="rolePermission">
              <div className="roleInputs">
                {/* Role */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Role</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'rolePermissions', 'canSearchRoles']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Search Roles</p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'rolePermissions', 'canAddRoles']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel"> Can Add Roles </p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'rolePermissions', 'canEditRoles']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel"> Can Edit Roles </p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </div>

                {/* Staff */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Staff</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'staffUserPermissions', 'canSearchStaff']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Search Staff</p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'staffUserPermissions', 'canAssignRoles']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Assign Roles </p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'staffUserPermissions', 'canBlockAccess']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Block Access</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'staffUserPermissions', 'canAddOrRemoveTags']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Add/Remove Tags</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </div>

                {/* Applicants */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Applicants</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'applicantUserPermissions', 'canSearchApplicants']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Search Applicants</p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item
                        name={['permissions', 'applicantUserPermissions', 'canUpdatePersonalInformation']}
                        valuePropName="checked"
                        style={{ margin: '0px', padding: '0px' }}
                      >
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Update Personal Information</p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'applicantUserPermissions', 'canBlockAccess']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Block Access</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div className="formBox">
                      <Form.Item name={['permissions', 'applicantUserPermissions', 'canAddOrRemoveTags']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          {' '}
                          <p className="checkBoxLabel">Can Add/Remove Tags</p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </div>

                {/* Entity */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Entity</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <div className="formBox">
                      <Form.Item name={['permissions', 'entityPermissions', 'canSearchEntities']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel">Can Search Entities</p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'entityPermissions', 'canAddEntities']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel"> Can Add Entities </p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'entityPermissions', 'canEditEntities']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel"> Can Edit Entities </p>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className="formBox">
                      <Form.Item name={['permissions', 'entityPermissions', 'canDeleteEntities']} valuePropName="checked" style={{ margin: '0px', padding: '0px' }}>
                        <Checkbox disabled={props.data.isDefault}>
                          <p className="checkBoxLabel"> Can Delete Entities </p>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
              <Button loading={formLoading || props.updateStaffRoleLoading} type="primary" htmlType="submit">
                Save Role
              </Button>
            </div>
          </div>
        </Card>
        {/* </div> */}
      </Form>
    </div>
  );
};
